import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import type { ReactNode } from 'react';
import React from 'react';
import type { Attachment } from 'sr-types/lib/production/v1/graphql';
import AttachmentReferenceComponent from './AttachmentReferenceComponent';
import type { AttachmentReferenceData, AttachmentReferenceType } from './attachmentReferenceUtils';
import { createAttachmentReferenceElement, getAttachmentReferenceType } from './attachmentReferenceUtils';
import {
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE
} from './lexicalConstants';

export type SerializedAttachmentReferenceNode = {
    referenceId: string;
    referenceType: AttachmentReferenceType;
    label: string;
    mimeType?: string;
    version: 1;
} & SerializedLexicalNode;

export class AttachmentReferenceNode extends DecoratorNode<ReactNode> {
    __referenceId: string;
    __referenceType: AttachmentReferenceType;
    __label: string;
    __mimeType?: string;

    static getType(): string {
        return 'attachment-reference';
    }

    static clone(node: AttachmentReferenceNode): AttachmentReferenceNode {
        return new AttachmentReferenceNode(
            node.__referenceId,
            node.__referenceType,
            node.__label,
            node.__mimeType,
            node.__key
        );
    }

    static importJSON(serializedNode: SerializedAttachmentReferenceNode): AttachmentReferenceNode {
        return $createAttachmentReferenceNode({
            referenceId: serializedNode.referenceId,
            referenceType: serializedNode.referenceType,
            label: serializedNode.label,
            mimeType: serializedNode.mimeType
        });
    }

    static importDOM(): DOMConversionMap | null {
        return {
            a: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute(MATERIAL_REFERENCE_ATTRIBUTE)) {
                    return null;
                }

                return {
                    conversion: (domNode: HTMLElement): DOMConversionOutput | null => {
                        const referenceId = domNode.getAttribute(MATERIAL_REFERENCE_ID_ATTRIBUTE);
                        if (!referenceId) {
                            return null;
                        }

                        const referenceType =
                            domNode.getAttribute(MATERIAL_REFERENCE_TYPE_ATTRIBUTE) === 'link' ? 'link' : 'attachment';
                        const label =
                            domNode.getAttribute(MATERIAL_REFERENCE_LABEL_ATTRIBUTE) || domNode.textContent || '';
                        const mimeType = domNode.getAttribute(MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE) || undefined;

                        return {
                            node: $createAttachmentReferenceNode({
                                referenceId,
                                referenceType,
                                label,
                                mimeType
                            })
                        };
                    },
                    priority: 3
                };
            }
        };
    }

    constructor(
        referenceId: string,
        referenceType: AttachmentReferenceType,
        label: string,
        mimeType?: string,
        key?: NodeKey
    ) {
        super(key);
        this.__referenceId = referenceId;
        this.__referenceType = referenceType;
        this.__label = label;
        this.__mimeType = mimeType;
    }

    createDOM(): HTMLElement {
        return document.createElement('span');
    }

    updateDOM(): false {
        return false;
    }

    exportDOM(): DOMExportOutput {
        return {
            element: createAttachmentReferenceElement({
                referenceId: this.__referenceId,
                referenceType: this.__referenceType,
                label: this.__label,
                mimeType: this.__mimeType
            })
        };
    }

    exportJSON(): SerializedAttachmentReferenceNode {
        return {
            type: AttachmentReferenceNode.getType(),
            referenceId: this.__referenceId,
            referenceType: this.__referenceType,
            label: this.__label,
            mimeType: this.__mimeType,
            version: 1
        };
    }

    getTextContent(): string {
        return this.__label;
    }

    getReferenceId(): string {
        return this.__referenceId;
    }

    isInline(): true {
        return true;
    }

    decorate(_editor: LexicalEditor, _config: EditorConfig): ReactNode {
        return (
            <AttachmentReferenceComponent
                nodeKey={this.__key}
                referenceId={this.__referenceId}
                referenceType={this.__referenceType}
                label={this.__label}
                mimeType={this.__mimeType}
            />
        );
    }
}

export const $createAttachmentReferenceNode = ({
    referenceId,
    referenceType,
    label,
    mimeType
}: AttachmentReferenceData): AttachmentReferenceNode =>
    $applyNodeReplacement(new AttachmentReferenceNode(referenceId, referenceType, label, mimeType));

export const $createAttachmentReferenceNodeFromAttachment = (
    attachment: Attachment
): AttachmentReferenceNode | null => {
    const referenceId = attachment.id;
    const label = attachment.name || attachment.entityReference?.label;

    if (!referenceId || !label) {
        return null;
    }

    return $createAttachmentReferenceNode({
        referenceId,
        referenceType: getAttachmentReferenceType(attachment),
        label,
        mimeType: attachment.type
    });
};

export const $isAttachmentReferenceNode = (node: LexicalNode | null | undefined): node is AttachmentReferenceNode => {
    return node instanceof AttachmentReferenceNode;
};
